import { useEffect, useRef } from "react";
import { handleScrollMilestoneEvent, handleEngagementHeartbeatEvent } from "../gainSightIntegration";

const throttle = (func, delay) => {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return func(...args);
  };
};

export const useArticleTracking = (selectedArticle) => {
  const pageLoadTime = useRef(null);
  const milestonesReached = useRef(new Set());
  const articleCompleted = useRef(false);
  const heartbeatInterval = useRef(null);
  const cumulativeEngagedTime = useRef(0);
  const heartbeatCounter = useRef(0);
  const isPageVisible = useRef(true);

  // Initialize tracking on mount
  useEffect(() => {
    pageLoadTime.current = Date.now();
    milestonesReached.current = new Set();
    articleCompleted.current = false;
    cumulativeEngagedTime.current = 0;
    heartbeatCounter.current = 0;
  }, [selectedArticle]);

  // Scroll milestone tracking
  useEffect(() => {
    const handleScroll = () => {
      if (!selectedArticle?.slug) return;

      const articleBody = document.getElementById("ck");
      if (!articleBody) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Calculate scroll percentage
      const scrollableHeight = documentHeight - windowHeight;
      const scrollPercentage = scrollableHeight > 0 ? (scrollTop / scrollableHeight) * 100 : 100;

      // Define milestones
      const milestones = [
        { percent: 25, label: "25" },
        { percent: 50, label: "50" },
        { percent: 75, label: "75" },
        { percent: 90, label: "90" },
        { percent: 99, label: "100" },
      ];

      // Check and fire events for reached milestones
      for (const { percent, label } of milestones) {
        if (scrollPercentage >= percent && !milestonesReached.current.has(label)) {
          milestonesReached.current.add(label);
          const timeToMilestone = Math.round((Date.now() - pageLoadTime.current) / 1000);
          
          // Fire article completed event when reaching 90%
          if (scrollPercentage >= 90 && !articleCompleted.current) {
            articleCompleted.current = true;
            handleScrollMilestoneEvent(selectedArticle.slug, timeToMilestone);
          }
          
          handleScrollMilestoneEvent(selectedArticle.slug, timeToMilestone, label);
        }
      }
    };

    // Throttle scroll handler to fire at most once every 200ms
    const throttledHandleScroll = throttle(handleScroll, 200);

    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [selectedArticle]);

  // Engagement heartbeat tracking
  useEffect(() => {
    if (!selectedArticle?.slug) return;

    const startHeartbeat = () => {
      if (heartbeatInterval.current) return; // Already running
            
      heartbeatInterval.current = setInterval(() => {
        if (isPageVisible.current && selectedArticle?.slug) {
          // Calculate cumulative engaged time
          cumulativeEngagedTime.current += 10;
          heartbeatCounter.current += 1;
          
          handleEngagementHeartbeatEvent(
            selectedArticle.slug,
            cumulativeEngagedTime.current,
            heartbeatCounter.current
          );
        }
      }, 10000); // Fire every 10 seconds
    };

    const stopHeartbeat = () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
        heartbeatInterval.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        isPageVisible.current = false;
        stopHeartbeat();
      } else {
        isPageVisible.current = true;
        startHeartbeat();
      }
    };

    // Start tracking when component mounts and article is available
    startHeartbeat();
    
    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopHeartbeat();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [selectedArticle]);
};
